import moment from 'moment';
import { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { fetchSummaryPeople, fetchSummaryPolicies } from '../../actions/SummaryActions';
import { ContentCard } from '../Cards';
import { formatCurrency } from '../../utils/CurrencyHelper';
import { stepNumberHelper } from '../../utils/StepNumberHelper';
import { fetchUpdatePackageProgress } from '../../actions/EnrollmentActions';
import { getTermsAcknowledgmentContentForPackage } from '../../apiCalls/enrollmentContent/enrollmentContentApiCalls';
import { FaRegQuestionCircle } from 'react-icons/fa';
import { fetchPostSignaturePackage, fetchPackageInfo  } from '../../actions/PackageActions';
import { openTermsAndConditionsModal } from '../../actions/ModalActions';
import { updateAllHarmony, afterHarmonyInit } from '../../actions/HarmonyActions';
import { HarmonyPolicyVolumeModal } from '../Modals/HarmonyPolicyVolumeModal';
import { IconContext } from 'react-icons';
import '../../styles/components.scss';
import { LoadingBlock } from '../LoadingBlock';
import { SignatureCapture } from './SignatureCapture/SignatureCapture';
import { PackageStepType } from '../../types/api/enrollmentPackages/Package';
import { EnrollmentPackageId, PolicyId, PolicySubscriptionId } from '../../types/api/primaryKeys/PrimaryKey';
import { useWolfTracsState } from '../../hooks/redux/useWolfTracsState';
import { PackageSummaryPolicy } from '../../types/api/enrollmentPackages/PackageSummaryPolicy';
import { PackageSummaryPerson } from '../../types/api/enrollmentPackages/PackageSummaryPerson';
import { Decimal } from '../../types/custom/Decimal';
import { CarrierImage } from '../Images/CarrierImage';
import { EnrollmentAgentType } from '../../types/enums/EnrollmentAgentType';
import { Nullable } from '../../types/custom/Nullable';
import { useReduxDispatch } from '../../hooks/redux/useReduxDispatch';
import { formatDate } from '../../utils/ViewHelper';

const currentStepName = PackageStepType.Summary;

interface Props {
	packageId: EnrollmentPackageId;
}

interface HarmonyModalLocalState {
	isModalOpen: boolean;
	policySubscriptionId: Nullable<PolicySubscriptionId>;
	enrollmentPackageId: Nullable<EnrollmentPackageId>;
	policyName: string;
	policyTotal: Decimal;
}

const viewTermsButtonText = 'View Terms';

const SummaryDetail  = ({ packageId }: Props) => {

	const [ showLoading, setShowLoading ] = useState(false);
	const [ showHarmonyLoading, setShowHarmonyLoading ] = useState(false);
	const [ didAgreeToTermsAndConditionsAcknowledgment, setDidAgreeToTermsAndConditionsAcknowledgment ] = useState(false);
	const [ termsAndConditionsAcknowledgmentContent, setTermsAndConditionsAcknowledgmentContent ] = useState<Nullable<string>>(null);

	const [ harmonyModalStatus, setHarmonyModalStatus ] = useState<HarmonyModalLocalState>({
		isModalOpen: false,
		policySubscriptionId: null,
		enrollmentPackageId: null,
		policyName: "",
		policyTotal: 0
	});

	const dispatch = useReduxDispatch();
	const history = useHistory();

	const { data: activePackage } = useWolfTracsState(s => s.packages.activePackage);
	const { data: peopleData } = useWolfTracsState(s => s.summary.summaryPeople);
	const { data: policiesData } = useWolfTracsState(s => s.summary.summaryPolicies);
	const harmonyState = useWolfTracsState(s => s.harmony);

	const shouldHideEmployeeContributions = activePackage === null || activePackage.hideEmployeeContributionInEnrollment;
    const shouldHideEmployerContributions = activePackage === null || activePackage.hideEmployerContributionInEnrollment;
	const shouldHideAllContributionInformation = shouldHideEmployeeContributions && shouldHideEmployerContributions;

	const [policiesUserHasViewedTermsFor, setPoliciesUserHasViewedTermsFor] = useState(new Set<PolicyId>());

	// If any policies have terms data to show, a checkbox is required to acknowledge.
	const policiesWithRequiredTermsAndConditions = policiesData?.filter(p => Boolean(p.termsAndConditionsContent)) ?? [];
	const isTermsAndConditionsCheckboxRequired = policiesWithRequiredTermsAndConditions.length > 0;
	const didUserViewAllPoliciesWithTermsAndConditions = policiesWithRequiredTermsAndConditions.every(p => policiesUserHasViewedTermsFor.has(p.policyId));

	const shouldSignatureBeBlocked = isTermsAndConditionsCheckboxRequired && !didAgreeToTermsAndConditionsAcknowledgment;

	useEffect(() => {
		const loadSummaryData = async () => {
			/* 
			/ always get the latest data if harmony plan exist
			/ this is important for harmony plans as fetchSummaryPolicies gets called only onMount and background update-elections might not pick up all election changes
			/ if harmony exists, await before continuing to ensure all the policies are updated
			/ we do not await on the other one because it can be done in the background but this one needs to display all the elections correctly so await here
			*/
			setShowHarmonyLoading(true);
	
			dispatch(afterHarmonyInit());
			const currentPackageInfo = await dispatch(fetchPackageInfo(packageId));
			if (currentPackageInfo?.steps) {
				const currentStepNumber = currentPackageInfo.lastCompletedStepNumber + 1;
				await dispatch(updateAllHarmony(packageId, null, currentPackageInfo, currentStepNumber));
				setShowHarmonyLoading(false);
			}
	
			// Start these in parallel.
			dispatch(fetchSummaryPeople(packageId));
			dispatch(fetchSummaryPolicies(packageId));
	
			const termsAndConditionsAcknowledgmentContent = await getTermsAcknowledgmentContentForPackage(packageId);
	
			setTermsAndConditionsAcknowledgmentContent(termsAndConditionsAcknowledgmentContent?.content ?? null);
		};

		loadSummaryData();
	}, [packageId]);

	useEffect(() => {
		// Goal of this effect:
		// If there is only one policy with terms and conditions to view, automatically open it to view.
		// This is purely a matter of convenience for the user.
		if (policiesWithRequiredTermsAndConditions.length === 1) {
			const onlyPolicyToViewTermsFor = policiesWithRequiredTermsAndConditions[0];

			// This condition is what prevents the modal from opening on every render.
			// If the user has already viewed the terms and conditions for this policy, don't open it again.
			if (!policiesUserHasViewedTermsFor.has(onlyPolicyToViewTermsFor.policyId)) {
				onTermsAndConditionsViewClick(onlyPolicyToViewTermsFor);
			}
		}
	}, [policiesWithRequiredTermsAndConditions, policiesUserHasViewedTermsFor]);

	const addPolicyToViewedTerms = (policyId: PolicyId) => {
		setPoliciesUserHasViewedTermsFor(prev => {
			const newSet = new Set(prev);
			newSet.add(policyId);
			return newSet;
		});
	};

	const onBack = () => {
		if (activePackage) {
			const currentStepNumber = stepNumberHelper.stepNumberForNamedStep(activePackage, currentStepName);
			const previousStep = stepNumberHelper.getPreviousStep(activePackage, currentStepNumber!);

			// This is a little hacky.
			// Basically: Check for the previous step. If the previous step has an entity id,
			// we are assuming it's a dynamic step that we can route to.
			// If it's not a dynamic step, we will hard code in a route to dependents.
			if (previousStep) {
				// Route to a dynamic step.
				if (previousStep.harmonyProductGroup) {
					history.push(`/packages/${packageId}/harmony/${previousStep.harmonyProductGroup}`);
				} else if (previousStep.relatedEntityId) {
					history.push(`/packages/${packageId}/plantypes/${previousStep.relatedEntityId}`);
				}
				// Assume dependents is the previous step (this should also be dynamic in the future)
				else {
					if (previousStep.type === "Insurability") {
						history.push(`/packages/${packageId}/insurability`);
					} else {
						history.push(`/packages/${packageId}/dependents`);
					}
				}
			}
		}
	};

	const onConfirmSignature = async (dataURL: string, isAutoGenerated: boolean) => {
		// ToDo: Why is this an invert instead of a hard-coded value...?
		setShowLoading(!showLoading);

		if (activePackage) {
			const postObj = {
				dataURL,
				isAutoGenerated
			};
			const currentStepNumber = stepNumberHelper.stepNumberForNamedStep(activePackage, PackageStepType.Finish);

			// ToDo: It looks like if the package fails to sign, this will just plow forward and update the progress anyway?
			await dispatch(fetchPostSignaturePackage(activePackage.enrollmentPackageId, postObj));
			await dispatch(fetchUpdatePackageProgress(activePackage.enrollmentPackageId, currentStepNumber, true));

			history.push(`/packages/${packageId}/finish`);
		}
	};

	const onTermsAndConditionsViewClick = ({ policyId, termsAndConditionsContent, planName }: PackageSummaryPolicy) => {
		// Flag that the user has clicked to view this particular policy's terms and conditions.
		addPolicyToViewedTerms(policyId);

		const modalTitle = `Terms and Conditions: ${planName}`;

		// Open the terms for the user to read.
		dispatch(openTermsAndConditionsModal(termsAndConditionsContent, modalTitle));
	};

	const onTermsAndConditionsCheckboxChanged = () => {
		setDidAgreeToTermsAndConditionsAcknowledgment(!didAgreeToTermsAndConditionsAcknowledgment);
	};

    const getEmployerData = (policiesData: Nullable<readonly PackageSummaryPolicy[]>) => {
		let employerTotal = 0;
		let employeePreTaxTotal = 0;
		let employeePostTaxTotal = 0;

		if (policiesData !== null) {
			policiesData.forEach(policy => {
				employerTotal += policy.employerContribution;
				employeePreTaxTotal += policy.employeePreTaxContribution;
				employeePostTaxTotal += policy.employeePostTaxContribution;
				employerTotal += policy.definedContributionUsed;
			});
		}

		return { employerTotal, employeePreTaxTotal, employeePostTaxTotal };
	};

	const renderPeople = (people: readonly PackageSummaryPerson[]) => {
		return people.map(person => {
			return (
				<tr key={person.personId}>
					<td>
						{person.name}
					</td>
					<td>
						{person.ssn}
					</td>
					<td>
						{formatDate(person.dateOfBirth)}
					</td>
					<td>
						{person.gender}
					</td>
					<td>
						{person.terminateDate !== null ? 'Relationship will be termed' : null}
					</td>
				</tr>
			);
		});
	};

	const renderHarmonyPolicyVolumeModal = () => {
		const { isModalOpen, policySubscriptionId, enrollmentPackageId, policyName, policyTotal } = harmonyModalStatus;
		return (<HarmonyPolicyVolumeModal showModal={isModalOpen} policySubscriptionId={policySubscriptionId} enrollmentPackageId={enrollmentPackageId} closeModal={closeHarmonyPolicyVolumeModal} policyName={policyName} policyTotal={policyTotal} />);
	};

	const closeHarmonyPolicyVolumeModal = () => {
		setHarmonyModalStatus({
			isModalOpen: false,
			policySubscriptionId: null,
			enrollmentPackageId: null,
			policyName: "",
			policyTotal: 0
		});
	};

	const showHarmonyPolicyVolumeModal = (policySubscriptionId: PolicySubscriptionId, enrollmentPackageId: EnrollmentPackageId, policyName: string, policyTotal: Decimal) => {
		setHarmonyModalStatus({
			isModalOpen: true,
			policySubscriptionId,
			enrollmentPackageId,
			policyName,
			policyTotal
		});
	};

	const renderVolume = (policy: PackageSummaryPolicy) => {
		if (policy.isHarmonyPlan) {
			return (
				<IconContext.Provider value={{ style: { marginBottom: '5px', marginLeft: '3px' } }}>
					<button className="btn btn-link p-0" onClick={() => showHarmonyPolicyVolumeModal(policy.policySubscriptionId, packageId, policy.planTypeName, policy.employeePreTaxContribution + policy.employeePostTaxContribution)}>
						See Policy Details
						<FaRegQuestionCircle />
					</button>
				</IconContext.Provider>);
		}
		if (policy.selectedVolume !== null && policy.selectedVolume > 0) {
			if (!policy.shouldDisplayVolumeAsPlainNumber) {
				// Note: This is BY FAR the common case.
				return "$" + formatCurrency(policy.selectedVolume);
			} else {
				// As of now this is just for PET plan types.
				return policy.selectedVolume;
			}
		} else {
			return renderPolicyParticipants(policy.participants);
		}
	};

	const renderPolicies = (policies: readonly PackageSummaryPolicy[]) => {
		return policies.map(policy => {
			return (
				<tr key={policy.policyId}>
					<td>
						<CarrierImage carrierLogoFileName={policy.carrierLogoFileName} altText={policy.carrierName} carrierId={policy.carrierId} />
					</td>
					<td>
						{policy.planTypeName}
						<br />
						{policy.planName}
					</td>
					<td>
						{moment(policy.effectiveDate).format("YYYY-MM-DD")}
					</td>
					<td>
						{renderVolume(policy)}
						{policy.rateTypeId === 4 ? <><span> / paycheck</span><div>${formatCurrency(policy.annualContribution)} / annual</div></> : null}
					</td>
					<td>
						{ /* Terms and conditions */}
						{
							policy.termsAndConditionsContent &&			
							<button onClick={() => onTermsAndConditionsViewClick(policy)} type="button" className="btn btn-link">
								{!policiesUserHasViewedTermsFor.has(policy.policyId) ? <strong>{viewTermsButtonText}</strong> : viewTermsButtonText}
							</button>
						}
					</td>
					<td>
						{policy.definedContributionUsed > 0 && <div><span>Defined Contribution used:</span><br /></div>}
						{!shouldHideEmployeeContributions && (policy.employeePreTaxContribution > 0 || policy.employeePostTaxContribution > 0) ? <div><span className="bold">You Pay:</span><br /></div> : null}
						{!shouldHideEmployerContributions && policy.employerContribution > 0 ? <span>Employer Pays:</span> : null}
					</td>
					<td className="text-right">
						{policy.definedContributionUsed > 0 && <div><span>${formatCurrency(policy.definedContributionUsed)}</span><br /></div>}
						{!shouldHideEmployeeContributions && (policy.employeePreTaxContribution > 0 || policy.employeePostTaxContribution > 0) ? <div><span className="bold">${formatCurrency(policy.employeePreTaxContribution + policy.employeePostTaxContribution)}</span><br /></div> : null}
						{!shouldHideEmployerContributions && policy.employerContribution > 0 ? <span>${formatCurrency(policy.employerContribution)}</span> : null}
					</td>
				</tr>
			);
		});
	};

	const renderPolicyParticipants = (participants: readonly string[]) => {
		return participants.map(name => {
			return (
				<span key={name}>{name}<br /></span>
			);
		});
	};

	const checkAidedByData = () => {
		let enrollmentAssistantName = null;
		let enrollmentAssistantId = null;
		let agentType = null;

		if(activePackage) {
			const agentInfo = activePackage.enrollmentTypeId ?? activePackage.aidedByEnrollmentTypeId;
			enrollmentAssistantName = activePackage.enrollmentAssistantName ?? activePackage.aidedByEnrollmentAssistantName;
			enrollmentAssistantId = activePackage.enrollmentAssistantId ?? activePackage.aidedByEnrollmentAssistantId;
			agentType = agentInfo ? agentInfo === EnrollmentAgentType.CallCenter ? 'Call Center Agent' : 'Agent' : null;
		}

		return { enrollmentAssistantId, enrollmentAssistantName, agentType };
	};


	// Main Render Logic Below /////////////////////////////////////////////////////////////////////////
	////////////////////////////////////////////////////////////////////////////////////////////////////
	const { employerTotal, employeePreTaxTotal, employeePostTaxTotal } = getEmployerData(policiesData);
	const { enrollmentAssistantId, enrollmentAssistantName, agentType } = checkAidedByData();

	if (!showHarmonyLoading && harmonyState.error) {
		return <h2>Session may have expired, please refresh the browser</h2>;
	}

	return (
		<>
			{renderHarmonyPolicyVolumeModal()}
			<ContentCard>
				<h2>Enrollment Summary</h2><br />
				<p className="mb-4">
					Please review the following information carefully, making sure it is all correct.
					You can revisit anytime within your enrollment period to make changes, add or remove subscriptions, dependents or contact information.
				</p>
				{enrollmentAssistantId && agentType && (
					<p className="mb-4">Enrollment Assistant ({agentType}) &mdash;<strong> {enrollmentAssistantName} | {enrollmentAssistantId} </strong></p>
				)}
				{enrollmentAssistantId && !agentType && (
					<p className="mb-4">Assisted by <strong>{enrollmentAssistantName}</strong> &mdash; {enrollmentAssistantId}</p>
				)}
				<div className="table-wrapper">
					<table className="table table-sm table-hover">
						<thead>
							<tr>
								<th>Name</th>
								<th>SSN</th>
								<th>Birthdate</th>
								<th>Gender</th>
								<th></th>
							</tr>
						</thead>
						<tbody>
							{peopleData && renderPeople(peopleData)}
						</tbody>
					</table>
				</div>
			</ContentCard>

			<ContentCard>
				<h2>Policy Selections</h2><br />
				{showHarmonyLoading && <LoadingBlock />}
				{!showHarmonyLoading &&
					<div className="table-wrapper">
						<table className="table table-sm table-hover mb-4">
							<thead>
								<tr>
									<th>Carrier</th>
									<th>Policy</th>
									<th>Effective Date</th>
									<th>Participants/Volume</th>
									<th />{ /* Terms and conditions */}
									<th colSpan={2}>{!shouldHideAllContributionInformation ? 'Contributions' : ''}</th>
								</tr>
							</thead>
							<tbody>
								{policiesData && renderPolicies(policiesData)}

								{!shouldHideEmployeeContributions && policiesData &&
									<tr>
										<td colSpan={2}></td>
										<td colSpan={3}>Total Deduction Per Paycheck</td>
										<td className="bold">Pre-Tax:<br />Post-Tax:</td>
										<td className="text-right bold">${formatCurrency(employeePreTaxTotal)}<br />${formatCurrency(employeePostTaxTotal)}</td>
									</tr>
								}

								{!shouldHideEmployerContributions && policiesData &&
									<tr>
										<td colSpan={2}></td>
										<td colSpan={3}>Total paid by Employer</td>
										<td colSpan={2} className="text-right">${formatCurrency(employerTotal)}</td>
									</tr>
								}
							</tbody>
						</table>
					</div>
				}
				{!showHarmonyLoading && !showLoading &&
					<>
						<div className="mb-4">
							{termsAndConditionsAcknowledgmentContent ?
								<div dangerouslySetInnerHTML={{ __html: termsAndConditionsAcknowledgmentContent }} />
								:
								<p>
									I have had the opportunity to review the above plans and to have my questions answered. I understand that if I decline coverage during this
									enrollment period, I may not be able to participate during the current Plan Year unless there is a Qualifying Event, such as loss of other
									coverage or change in family composition.
								</p>
							}
						</div>
						{
							isTermsAndConditionsCheckboxRequired &&
							<div className="tc">
								{!didUserViewAllPoliciesWithTermsAndConditions ?
								<>
									<strong>View all of the Terms and Conditions for each of the above policies to continue.</strong>
								</> :
								<>
									<input checked={didAgreeToTermsAndConditionsAcknowledgment} onChange={() => onTermsAndConditionsCheckboxChanged()} className="mr-4" type="checkbox" />
									<strong>I have read and agreed to the Terms and Conditions provided on each of the above policies.</strong>
								</>
								}
							</div>
						}
					</>
				}
				{showLoading &&
					<LoadingBlock />
				}
			</ContentCard>
			{activePackage && !showHarmonyLoading && !shouldSignatureBeBlocked && <SignatureCapture showLoading={showLoading} name={activePackage.personName} onBack={onBack} onSubmitCallBack={onConfirmSignature}/>}
		</>
	);
};

export default SummaryDetail;